import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useAnimatedScrollHandler } from 'react-native-reanimated';
import { ModalFlashList } from 'design-system/ModalScrollables';
import Modal from 'utils/packages/Modal';
import { rem } from 'design-system';
import IconEmmaWallet from 'design-system/icons/IconEmmaWallet';
import { useAppSelector } from 'store/hooks';
import { formatAmount } from 'utils/formatting';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useBottomOffset } from 'hooks/useBottomOffset';
import { useScrollableModalMaxHeight } from 'hooks/useScrollableModalMaxHeight';
import TransferAccountRow from 'features/transfers/components/TransferAccountRow';
import { selectActiveTradingSecclAccountsSorted } from '../reducers/selectors';
import { getTradingAccountName } from '../utils';
const estimatedItemSize = rem(77);
const keyExtractor = (item) => item.account.id;
const getAllowanceDesc = (account) => {
    if (account.accountType === 'ISA') {
        const allowance = account.isaRemainingAllowanceAfterPending;
        return allowance && allowance.value > 0
            ? `${formatAmount(allowance.value, allowance.currency, 2)} of allowance left`
            : 'No allowance left';
    }
    if (account.accountType === 'JISA') {
        const allowance = account.jisaRemainingAllowanceAfterPending;
        return allowance && allowance.value > 0
            ? `${formatAmount(allowance.value, allowance.currency, 2)} of allowance left`
            : 'No allowance left';
    }
    return 'No annual limit';
};
const TradingAccountToMoveMoneyToPickerModalContent = ({ current, onSelect, sharedValue, }) => {
    const accounts = useAppSelector(selectActiveTradingSecclAccountsSorted);
    const bottom = useBottomOffset();
    const contentContainerStyle = useMarginBottom('paddingBottom');
    const scrollHandler = useAnimatedScrollHandler((handle) => {
        // eslint-disable-next-line no-param-reassign
        sharedValue.value = handle.contentOffset.y;
    }, []);
    const handleItemPress = useCallback((item) => () => {
        onSelect(item.account);
        Modal.hide();
    }, [onSelect]);
    const renderItem = useCallback(({ item }) => {
        const disabled = 
        // eslint-disable-next-line no-nested-ternary
        item.account.accountType === 'JISA'
            ? (item.account.jisaRemainingAllowanceAfterPending?.value ?? 0) <= 0
            : item.account.accountType === 'ISA'
                ? (item.account.isaRemainingAllowanceAfterPending?.value ?? 0) <= 0
                : false;
        return (<TransferAccountRow disabled={disabled} Icon={<IconEmmaWallet />} onPress={handleItemPress(item)} desc={getAllowanceDesc(item.account)} selected={item.account.id === current} name={getTradingAccountName(item.account)}/>);
    }, [current, handleItemPress]);
    const maxHeight = useScrollableModalMaxHeight();
    const listHeight = useMemo(() => Math.min(maxHeight, accounts.length * estimatedItemSize + bottom), [maxHeight, accounts.length, bottom]);
    const style = useMemo(() => ({ height: listHeight }), [listHeight]);
    return (<View style={style}>
      <ModalFlashList data={accounts} renderItem={renderItem} scrollEventThrottle={16} onScroll={scrollHandler} keyExtractor={keyExtractor} keyboardShouldPersistTaps="handled" contentContainerStyle={contentContainerStyle}/>
    </View>);
};
export const TradingAccountToMoveMoneyToPickerModal = {
    show: (current, onSelect, sharedValue, onDidHideCallback) => {
        Modal.show(<TradingAccountToMoveMoneyToPickerModalContent current={current} onSelect={onSelect} sharedValue={sharedValue}/>, {
            containerStyle: styles.modal,
            title: 'Select an investment account',
            scrollable: {
                scrollValue: sharedValue,
            },
            onDidHideCallback,
        });
    },
    hide: () => {
        Modal.hide();
    },
};
const styles = StyleSheet.create({
    modal: {
        paddingHorizontal: 0,
        overflow: 'hidden',
    },
});
